<template>
    <Modal class="simulate-payyo-payment-modal"
           icon="fa-solid fa-face-lying"
           title="PAYYO.SIMULATE_MODAL_TITLE"
           :cancel="cancel">
        <h3>Standard Test Cards</h3>
        <SimulatePayyoPaymentCardsTable :cards="buildCardData(standardTestCards)"
                                        btn-color="success"
                                        :simulating-card-number="simulatingCardNumber"
                                        @simulate="simulate" />

        <h3>Test Cards For Specific Success Cases</h3>
        <SimulatePayyoPaymentCardsTable :cards="buildCardData(testCardsForSpecificSuccessCases)"
                                        btn-color="info"
                                        :simulating-card-number="simulatingCardNumber"
                                        @simulate="simulate" />

        <h3>Test Cards For Specific Error Cases</h3>
        <SimulatePayyoPaymentCardsTable :cards="buildCardData(testCardsForSpecificErrorCases)"
                                        btn-color="danger"
                                        :simulating-card-number="simulatingCardNumber"
                                        @simulate="simulate" />
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import PayyoTransactionType from '@/lib/PaymentMethods/Payyo/Enums/PayyoTransactionType';
import SimulatePayyoPaymentCardsTable from '@/epos/vue/modals/Payment/PaymentMethods/Payyo/SimulatePayyoPaymentCardsTable';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { standardTestCards, testCardsForSpecificErrorCases, testCardsForSpecificSuccessCases } from './simulatedCards';

const expMonth = parseInt(String(new Date().getMonth() + 1).padStart(2, '0'), 10);
const expYear = parseInt(String(new Date().getFullYear() + 1).slice(-2), 10);

export default {
    components: {
        SimulatePayyoPaymentCardsTable,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {PayyoTransaction}
         */
        transaction: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            simulatingCardNumber: null,

            standardTestCards,
            testCardsForSpecificSuccessCases,
            testCardsForSpecificErrorCases,
        };
    },

    computed: {
        transactionType() {
            if (this.transaction.payment_methods.hasOwnProperty(PayyoTransactionType.CARD_MOTO)) {
                return PayyoTransactionType.CARD_MOTO;
            }
            return PayyoTransactionType.CARD_PRESENT;
        },
    },

    methods: {
        async simulate(cardData) {
            const data = {};

            if (this.transactionType === PayyoTransactionType.CARD_MOTO) {
                // MOTO simulating requires a whole card object with expiry and cvv (generated in buildCardData).
                data.card = cardData.card;
                this.simulatingCardNumber = cardData.card.number;
            } else if (this.transactionType === PayyoTransactionType.CARD_PRESENT) {
                // Cardholder present simulating only needs the card number.
                data.card_present = {
                    number: cardData.card_number,
                };
                this.simulatingCardNumber = cardData.card_number;
            }

            await this.makeSimulateRequest(data);
        },

        async makeSimulateRequest(data) {
            try {
                await container.payyoDriver.simulatePayment(
                    this.transaction,
                    data,
                );
                this.resolve();
            } catch (e) {
                if (e.response?.data) {
                    const message = [
                        e.response.data.error,
                        e.response.data.message,
                    ].join(' ').trim();

                    container.toastFactory.errorTop('', message || e.message);
                }
            } finally {
                this.simulatingCardNumber = null;
            }
        },

        buildCardData(data) {
            // The simulated cards aren't always applicable.
            data = data.filter((cardData) => {
                if (cardData.hide_for && cardData.hide_for === this.transactionType) {
                    return false;
                }
                return true;
            });

            if (this.transactionType === PayyoTransactionType.CARD_MOTO) {
                return data.map((cardData) => {
                    return {
                        ...cardData,
                        card: {
                            number: cardData.card_number,
                            exp_month: cardData.exp_month || expMonth,
                            exp_year: cardData.exp_year || expYear,
                            cvc: cardData.cvc || '123',
                        },
                    };
                });
            }
            return data;
        },
    },
};
</script>

<style lang="less">
.simulate-payyo-payment-modal {
    h3 {
        font-size: 18px;
        font-weight: bold;
        margin: 30px 0 5px;

        &:first-child {
            margin-top: 0;
        }
    }

    .table {
        font-size: 12px;
        user-select: text; // Allow copying card numbers and text.

        td,
        th {
            // Card number cell
            &:first-child {
                width: 140px;
            }

            // Button cell
            &:last-child {
                width: 100px;
                text-align: right;
            }
        }
    }
}
</style>
